import { useLocalization } from '@/Hooks';
import { PagesRouting } from '@/Routing';
import { useMemo } from 'react';

export type RouteItemType = {
  title: string;
  route: string;
  children?: RouteItemType[];
};

export const useRouteItems = () => {
  const {
    modules,
    onlineArchive,
    warehousePage,
    settingsPage: { Items, OnlineArchiveSettings, ShippingAutomationSettings },
  } = useLocalization();

  const findPathAndParents = (routes: RouteItemType[], path: string): string[] | null => {
    for (const { title, route, children } of routes) {
      if (route === path) {
        return [title];
      }

      if (children && children.length > 0) {
        const childResult = findPathAndParents(children, path);
        if (childResult) {
          return [title, ...childResult];
        }
      }
    }

    return null;
  };

  const routeItems = useMemo<RouteItemType[]>(() => {
    return [
      {
        title: modules.Dashboard,
        route: PagesRouting.MainPages.DashboardPage.Dashboard,
      },
      {
        title: modules.Sales,
        route: PagesRouting.MainPages.SalesPage.Sales,
      },
      {
        title: modules.Purchases,
        route: PagesRouting.MainPages.PurchasesPage,
      },
      {
        title: modules.Shop,
        route: PagesRouting.MainPages.ShopPage.Shop,
      },
      {
        title: modules.Messages,
        route: PagesRouting.MainPages.MessagesPage,
      },
      {
        title: modules.Accounting,
        route: PagesRouting.MainPages.OnlineArchivePage.OnlineArchive,
        children: [
          {
            title: onlineArchive.Tabs.Upload,
            route: PagesRouting.MainPages.OnlineArchivePage.UploadTab,
          },
          {
            title: onlineArchive.Tabs.Allocate,
            route: PagesRouting.MainPages.OnlineArchivePage.AllocateTab,
          },
          {
            title: onlineArchive.Tabs.Archive,
            route: PagesRouting.MainPages.OnlineArchivePage.ArchiveTab,
          },
          {
            title: onlineArchive.Tabs.Reports,
            route: PagesRouting.MainPages.OnlineArchivePage.ReportsTab,
          },
        ],
      },
      {
        title: modules.Listing,
        route: PagesRouting.MainPages.ListingPage,
      },
      {
        title: modules.Logistic,
        route: PagesRouting.MainPages.LogisticsPage.Logistics,
      },
      {
        title: modules.Products,
        route: PagesRouting.MainPages.ProductsPage.Products,
      },
      {
        title: modules.Warehouse,
        route: PagesRouting.MainPages.WarehousePage.Warehouse,
        children: [
          {
            title: warehousePage.Tabs.Warehouse,
            route: PagesRouting.MainPages.WarehousePage.UnitsTab,
          },
          {
            title: warehousePage.Tabs.Reports,
            route: PagesRouting.MainPages.WarehousePage.ReportsTab,
          },
        ],
      },
      {
        title: modules.Customers,
        route: PagesRouting.MainPages.CustomersPage.Customers,
      },
      {
        title: modules.Suppliers,
        route: PagesRouting.MainPages.SuppliersPage.Suppliers,
      },
      {
        title: modules.DesignService,
        route: PagesRouting.MainPages.DesignServicePage,
      },
      {
        title: modules.Settings,
        route: PagesRouting.SettingsPages.Settings,
        children: [
          {
            title: Items.Account,
            route: PagesRouting.SettingsPages.AccountSettings,
          },
          {
            title: Items.Synchronize,
            route: PagesRouting.SettingsPages.SynchronizeSettings,
          },
          {
            title: Items.Mandats,
            route: PagesRouting.SettingsPages.MandatsSettings,
          },
          {
            title: Items.OrderAutomation,
            route: PagesRouting.SettingsPages.OrderAutomationSettings,
          },
          {
            title: Items.Logistic,
            route: PagesRouting.SettingsPages.LogisticSettings,
          },
          {
            title: Items.ShippingAutomation,
            route: PagesRouting.SettingsPages.ShippingAutomationSettings.ShippingAutomation,
            children: [
              {
                title: ShippingAutomationSettings.Tabs.AmountAutomation,
                route: PagesRouting.SettingsPages.ShippingAutomationSettings.AmountAutomation,
              },
              {
                title: ShippingAutomationSettings.Tabs.ItemsCategoryShippingAutomation,
                route: PagesRouting.SettingsPages.ShippingAutomationSettings.CategoryAutomation,
              },
              {
                title: ShippingAutomationSettings.Tabs.ItemsCategoryPackagingAutomation,
                route: PagesRouting.SettingsPages.ShippingAutomationSettings.PackagingAutomation,
              },
              {
                title: ShippingAutomationSettings.Tabs.MixCategoriesAutomation,
                route: PagesRouting.SettingsPages.ShippingAutomationSettings.MixCategoryAutomation,
              },
            ],
          },
          {
            title: Items.AddressBook,
            route: PagesRouting.SettingsPages.AddressBookSettings,
          },
          {
            title: Items.Packages,
            route: PagesRouting.SettingsPages.PackagesSettings,
          },
          {
            title: Items.Marketplaces,
            route: PagesRouting.SettingsPages.MarketplacesSettings,
          },
          {
            title: Items.Accounting,
            route: PagesRouting.SettingsPages.OnlineArchiveSetting.OnlineArchive,
            children: [
              {
                title: OnlineArchiveSettings.General.Title,
                route: PagesRouting.SettingsPages.OnlineArchiveSetting.General,
              },
              {
                title: OnlineArchiveSettings.DocumentCategories.Title,
                route: PagesRouting.SettingsPages.OnlineArchiveSetting.DocumentCategories,
              },
              {
                title: OnlineArchiveSettings.AllocateCategories.Title,
                route: PagesRouting.SettingsPages.OnlineArchiveSetting.AllocateCategories,
              },
              {
                title: OnlineArchiveSettings.UploadTemplates.Title,
                route: PagesRouting.SettingsPages.OnlineArchiveSetting.UploadTemplates,
              },
              {
                title: OnlineArchiveSettings.AccountChart.Title,
                route: PagesRouting.SettingsPages.OnlineArchiveSetting.AccountChart,
              },
              {
                title: OnlineArchiveSettings.ScanDocuments.Title,
                route: PagesRouting.SettingsPages.OnlineArchiveSetting.ScanDocuments,
              },
            ],
          },
          {
            title: Items.Warehouse,
            route: PagesRouting.SettingsPages.WarehouseSettings,
          },
          {
            title: Items.Products,
            route: PagesRouting.SettingsPages.ProductsSettings.Categories,
          },
          {
            title: Items.Invoices,
            route: PagesRouting.SettingsPages.InvoicesSettings,
          },
          {
            title: Items.Feedbacks,
            route: PagesRouting.SettingsPages.FeedbacksSettings,
          },
          {
            title: Items.GridsColours,
            route: PagesRouting.SettingsPages.GridsColorsSettings,
          },
          {
            title: Items.Integrations,
            route: PagesRouting.SettingsPages.IntegrationsSettings,
          },
          {
            title: Items.ShipCheaperApi,
            route: PagesRouting.SettingsPages.ShipCheaperApiSettings,
          },
          {
            title: Items.Help,
            route: PagesRouting.SettingsPages.HelpSettings,
          },
          {
            title: Items.Info,
            route: PagesRouting.SettingsPages.InfoSettings,
          },
          {
            title: Items.DocumentTemplates,
            route: PagesRouting.SettingsPages.DocumentTemplateSettings,
          },
          {
            title: Items.EmailTemplates,
            route: PagesRouting.SettingsPages.EmailTemplateSettings,
          },
        ],
      },
    ];
  }, [modules]);

  return {
    routeItems,
    findPathAndParents,
  };
};
